import { useState, useMemo, Fragment, lazy, Suspense } from "react";
import { Euro, TrendingUp, Car, Wrench, Building2, BarChart3, Award, ChevronDown, ChevronRight } from "lucide-react";
import { fmtEUR } from "@/lib/format";
import { KpiCard, Widget, Priorites, DetailModal, chargerDetailKpi, TendanceSub } from "@/components/dashboards/commun";
import { Button } from "@/components/ui/button";

const TendancesChart = lazy(() => import("@/components/dashboards/TendancesChart"));

const eur = (n) => fmtEUR(n ?? 0);
const nbf = (n) => (n === null || n === undefined ? "—" : n);
const slug = (s) => s.replace(/ /g, "-");
const PERIODE_LABELS = { jour: "aujourd'hui", semaine: "semaine en cours", mois: "mois en cours" };

const SousTable = ({ titre, lignes, testId }) => (
  <div className="rounded-lg border border-[--idc-border] bg-white p-2.5" data-testid={testId}>
    <div className="text-[11px] font-bold tracking-wide text-[--idc-muted] uppercase mb-1.5">{titre}</div>
    <table className="w-full text-[12px]">
      <thead>
        <tr className="text-[10.5px] text-[--idc-muted] text-left">
          <th className="pb-1 font-medium">Type</th>
          <th className="pb-1 text-right font-medium">Nb</th>
          <th className="pb-1 text-right font-medium">CA</th>
        </tr>
      </thead>
      <tbody>
        {lignes.map((l) => (
          <tr key={l.label} className={l.total ? "border-t border-[--idc-border] font-bold" : ""}>
            <td className="py-0.5">{l.label}</td>
            <td className="py-0.5 text-right">{nbf(l.nb)}</td>
            <td className="py-0.5 text-right whitespace-nowrap">{eur(l.ca)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const lignesVehicules = (d) => [
  { label: "VN", nb: d?.vn?.nb ?? 0, ca: d?.vn?.ca },
  { label: "VD", nb: d?.vd?.nb ?? 0, ca: d?.vd?.ca },
  { label: "VO", nb: d?.vo?.nb ?? 0, ca: d?.vo?.ca },
  { label: "Total", nb: d?.total?.nb ?? 0, ca: d?.total?.ca, total: true },
];

const DetailSite = ({ detail, site }) => (
  <tr className="bg-[#F8FAFD]" data-testid={`comparatif-detail-${slug(site)}`}>
    <td colSpan={11} className="px-2 py-2.5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
        <SousTable titre="Commandes" lignes={lignesVehicules(detail?.commandes)} testId={`detail-commandes-${slug(site)}`} />
        <SousTable titre="Livraisons" lignes={lignesVehicules(detail?.livraisons)} testId={`detail-livraisons-${slug(site)}`} />
        <SousTable titre="APV" testId={`detail-apv-${slug(site)}`} lignes={[
          { label: "Mécanique", nb: detail?.apv?.mecanique?.nb ?? 0, ca: detail?.apv?.mecanique?.ca },
          { label: "Carrosserie", nb: detail?.apv?.carrosserie?.nb ?? 0, ca: detail?.apv?.carrosserie?.ca },
          { label: "PDR", nb: detail?.apv?.pdr?.nb, ca: detail?.apv?.pdr?.ca },
          { label: "Total", nb: detail?.apv?.total?.nb ?? 0, ca: detail?.apv?.total?.ca, total: true },
        ]} />
      </div>
    </td>
  </tr>
);

const EcartPill = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="text-[--idc-muted]">—</span>;
  const neg = pct < 0;
  return (
    <span className="pill" title="Écart de CA vs meilleur site"
      style={{ background: neg ? "#FEE2E2" : "#DCFCE7", color: neg ? "#B91C1C" : "#15803D" }}>
      {pct > 0 ? "+" : ""}{pct} %
    </span>
  );
};

const Jauge = ({ label, pct, testId }) => {
  const c = pct === null || pct === undefined ? "#94A3B8" : pct >= 100 ? "#16A34A" : pct >= 70 ? "#D97706" : "#DC2626";
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <span className="text-[10px] text-[--idc-muted] w-7 shrink-0">{label}</span>
      <div className="w-16 h-1.5 rounded-full bg-[#E5EAF2] overflow-hidden shrink-0">
        <div className="h-full rounded-full transition-[width]" style={{ width: `${Math.min(pct ?? 0, 100)}%`, background: c }} />
      </div>
      <span className="text-[10.5px] font-bold whitespace-nowrap" style={{ color: c }}>
        {pct === null || pct === undefined ? "—" : `${pct} %`}
      </span>
    </div>
  );
};

const CelluleObjectifs = ({ s }) => (
  s.objectifs ? (
    <div className="space-y-1" data-testid={`objectifs-${slug(s.site)}`}
      title={`Objectifs mensuels : CA ${fmtEUR(s.objectifs.ca_objectif ?? 0)} · ${s.objectifs.livraisons_objectif ?? 0} livraison(s) — réalisé : ${fmtEUR(s.objectifs.ca_mois ?? 0)} · ${s.objectifs.livraisons_mois ?? 0} livraison(s)`}>
      <Jauge label="CA" pct={s.objectifs.atteinte_ca_pct} testId={`jauge-ca-${slug(s.site)}`} />
      <Jauge label="Livr." pct={s.objectifs.atteinte_livraisons_pct} testId={`jauge-livraisons-${slug(s.site)}`} />
    </div>
  ) : <span className="text-[--idc-muted]">—</span>
);

const lireOuverts = () => {
  try { return JSON.parse(sessionStorage.getItem("dg-comparatif-ouverts")) || []; } catch { return []; }
};

const ComparatifSites = ({ data, periode, onDrill }) => {
  const [ouverts, setOuverts] = useState(lireOuverts);
  const toggle = (site) => setOuverts((prev) => {
    const next = prev.includes(site) ? prev.filter((s) => s !== site) : [...prev, site];
    sessionStorage.setItem("dg-comparatif-ouverts", JSON.stringify(next));
    return next;
  });
  const total = data.total_groupe;
  const LigneSynthese = ({ s, isTotal }) => (
    <tr className={`border-t border-[--idc-border] ${isTotal ? "bg-[#EEF3FB] font-extrabold" : ""}`}
      data-testid={isTotal ? "comparatif-total-row" : `comparatif-row-${slug(s.site)}`}>
      <td className="py-2 pl-1">
        <button onClick={() => toggle(s.site)} className="flex items-center gap-1.5 font-semibold hover:text-[#2F6FED] transition-colors"
          data-testid={`comparatif-toggle-${slug(s.site)}`} aria-expanded={ouverts.includes(s.site)}>
          {ouverts.includes(s.site) ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
          <span className="whitespace-nowrap">{s.site}</span>
        </button>
      </td>
      <td className="py-2 text-right font-bold whitespace-nowrap">{eur(s.ca_global)}</td>
      <td className="py-2 text-right whitespace-nowrap">{eur(s.ca_commerce)}</td>
      <td className="py-2 text-right whitespace-nowrap">{eur(s.ca_apv)}</td>
      <td className="py-2 text-right whitespace-nowrap">{eur(s.marge)}</td>
      <td className="py-2 text-right">{s.nb_commandes ?? 0}</td>
      <td className="py-2 text-right">{s.nb_livraisons ?? 0}</td>
      <td className="py-2 text-right">{s.nb_or ?? 0}</td>
      <td className="py-2 text-right"><EcartPill pct={s.ecart_pct} /></td>
      <td className="py-2 pl-4"><CelluleObjectifs s={s} /></td>
      <td className="py-2 text-right pr-1">
        {!isTotal && (
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onDrill(s.site)}
            data-testid={`drill-${slug(s.site)}`}>Voir le site</Button>
        )}
      </td>
    </tr>
  );
  return (
    <Widget titre={`Comparatif des sites (${PERIODE_LABELS[periode] || periode})`} icon={Building2} testId="widget-comparatif-sites">
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full min-w-[920px] text-[12.5px]">
          <thead>
            <tr className="text-[11px] text-[--idc-muted] text-left">
              <th className="pb-1.5 pl-1">Site</th>
              <th className="pb-1.5 text-right">CA global</th>
              <th className="pb-1.5 text-right">CA Commerce</th>
              <th className="pb-1.5 text-right">CA APV</th>
              <th className="pb-1.5 text-right">Marge</th>
              <th className="pb-1.5 text-right">Cmdes</th>
              <th className="pb-1.5 text-right">Livr.</th>
              <th className="pb-1.5 text-right">OR APV</th>
              <th className="pb-1.5 text-right">Écart</th>
              <th className="pb-1.5 pl-4">Objectifs (mois)</th>
              <th className="pb-1.5" />
            </tr>
          </thead>
          <tbody>
            {data.sites.map((s) => (
              <Fragment key={s.site}>
                <LigneSynthese s={s} />
                {ouverts.includes(s.site) && <DetailSite detail={s.detail} site={s.site} />}
              </Fragment>
            ))}
            {total && (
              <Fragment>
                <LigneSynthese s={total} isTotal />
                {ouverts.includes(total.site) && <DetailSite detail={total.detail} site={total.site} />}
              </Fragment>
            )}
          </tbody>
        </table>
      </div>
    </Widget>
  );
};

const TendancesChartInline = ({ tendances }) => (
  <Suspense fallback={<div style={{ height: 300 }} className="flex items-center justify-center text-[12px] text-[--idc-muted]">Chargement du graphique…</div>}>
    <TendancesChart tendances={tendances} />
  </Suspense>
);

export const DashDG = ({ d, onDrill }) => {
  const [detail, setDetail] = useState(null);
  const k = d.kpis, w = d.widgets;
  const ouvrir = (cle) => chargerDetailKpi(cle, setDetail);
  return (
    <div data-testid="dash-dg">
      {w.priorites && <Priorites items={w.priorites} titre="Synthèse exécutive" />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {k.ca_groupe && <KpiCard icon={Euro} couleur="#2F6FED" titre="CA groupe (mois)" valeur={eur(k.ca_groupe.valeur)} testId="kpi-ca-groupe" onClick={() => ouvrir("ca_groupe")} sub={<TendanceSub kpi={k.ca_groupe} periode="mois" />} />}
        {k.marge_groupe && <KpiCard icon={TrendingUp} couleur="#16A34A" titre="Marge groupe" valeur={eur(k.marge_groupe.valeur)} testId="kpi-marge-groupe" onClick={() => ouvrir("marge_groupe")} sub={<TendanceSub kpi={k.marge_groupe} periode="mois" />} />}
        {k.ventes_groupe && <KpiCard icon={Car} couleur="#E8853D" titre="Ventes groupe" valeur={k.ventes_groupe.valeur ?? 0} testId="kpi-ventes-groupe" onClick={() => ouvrir("ventes_groupe")} sub={<TendanceSub kpi={k.ventes_groupe} periode="mois" />} />}
        {k.ca_atelier_groupe && <KpiCard icon={Wrench} couleur="#7C5CE0" titre="CA atelier groupe" valeur={eur(k.ca_atelier_groupe.valeur)} testId="kpi-ca-atelier-groupe" onClick={() => ouvrir("ca_atelier_groupe")} sub={<TendanceSub kpi={k.ca_atelier_groupe} periode="mois" />} />}
      </div>
      <DetailModal detail={detail} onClose={() => setDetail(null)} />
      <div className="grid grid-cols-1 gap-4">
        {w.comparatif_sites?.sites && <ComparatifSites data={w.comparatif_sites} periode={d.periode} onDrill={onDrill} />}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {w.tendances?.mois && (
            <Widget titre="Tendances CA — 6 derniers mois (groupe)" icon={BarChart3} testId="widget-tendances">
              <TendancesChartInline tendances={w.tendances} />
            </Widget>
          )}
          {w.top_flop && (
            <Widget titre="Top / Flop" icon={Award} testId="widget-top-flop">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="rounded-lg p-3" style={{ background: "#DCFCE710", border: "1px solid #BBF7D0" }}>
                  <div className="text-[11px] font-bold text-[#15803D]">TOP — Meilleur vendeur du mois</div>
                  <div className="text-[14px] font-extrabold mt-1">{w.top_flop.top?.vendeur || "—"}</div>
                  <div className="text-[12px] text-[--idc-muted]">{w.top_flop.top?.ventes ?? 0} vente(s)</div>
                </div>
                <div className="rounded-lg p-3" style={{ background: "#FEE2E210", border: "1px solid #FECACA" }}>
                  <div className="text-[11px] font-bold text-[#B91C1C]">FLOP — VO le plus âgé en stock</div>
                  <div className="text-[14px] font-extrabold mt-1">{w.top_flop.flop?.libelle || "—"}</div>
                  <div className="text-[12px] text-[--idc-muted]">{w.top_flop.flop ? `${w.top_flop.flop.jours} j · ${w.top_flop.flop.site || ""}` : ""}</div>
                </div>
              </div>
            </Widget>
          )}
        </div>
      </div>
    </div>
  );
};
